import { useState } from 'react';
import { useLang } from '../../../i18n/LanguageContext';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactStatus, setContactStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const { t } = useLang();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new URLSearchParams();
    formData.append('email', email);
    try {
      const response = await fetch('https://readdy.ai/api/form/d6ndt705dsf7sr1n6n00', {
        method: 'POST',
        body: formData,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      if (response.ok) { setSubmitted(true); setEmail(''); }
    } catch { /* silent */ }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactStatus('submitting');
    const formData = new URLSearchParams();
    formData.append('name', contactForm.name);
    formData.append('email', contactForm.email);
    formData.append('message', contactForm.message);
    try {
      const res = await fetch('https://readdy.ai/api/form/d72bcq4mc4tbhfnc6bq0', {
        method: 'POST',
        body: formData,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      if (res.ok) {
        setContactStatus('success');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        setContactStatus('error');
      }
    } catch {
      setContactStatus('error');
    }
  };

  return (
    <>
      <footer className="relative bg-[#0d0221] border-t border-[#d4af37]/30 pt-10 sm:pt-16 pb-8 px-4 sm:px-8">
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-10 gap-8 sm:gap-12 mb-12">
            <div className="md:col-span-4">
              <h3 className="text-3xl sm:text-5xl font-thin text-white mb-4">{t('Subscribe to Our Newsletter', '订阅我们的通讯')}</h3>
              <p className="text-[#c8b6ff] mb-6">{t('Get the latest courses, events and mystical knowledge', '获取最新课程、活动与神秘学知识')}</p>
              {submitted ? (
                <div className="flex items-center space-x-3 text-[#d4af37]">
                  <i className="ri-checkbox-circle-line text-2xl"></i>
                  <span>{t('Thank you for subscribing!', '感谢您的订阅！')}</span>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} data-readdy-form id="newsletter-form" className="space-y-4">
                  <div className="relative">
                    <input type="email" name="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('Enter your email address', '请输入您的邮箱地址')}
                      className="w-full px-0 py-3 bg-transparent border-b-2 border-[#d4af37] text-white placeholder-gray-400 text-sm focus:outline-none focus:border-[#f4d03f] transition-colors" />
                  </div>
                  <button type="submit" className="px-8 py-3 bg-white text-black rounded-full hover:bg-[#d4af37] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap font-medium flex items-center space-x-2">
                    <span>{t('Subscribe', '订阅')}</span>
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </form>
              )}
            </div>

            <div className="md:col-span-3">
              <div className="space-y-3">
                <a href="/courses" className="block text-white hover:text-[#d4af37] transition-colors cursor-pointer">{t('Course Center', '课程中心')}</a>
                <a href="/membership" className="block text-white hover:text-[#d4af37] transition-colors cursor-pointer">{t('Student Benefits', '学员优惠')}</a>
                <a href="/certificate" className="block text-white hover:text-[#d4af37] transition-colors cursor-pointer">{t('Verify Certificate', '证书查询')}</a>
                <button
                  onClick={() => { setContactOpen(true); setContactStatus('idle'); }}
                  className="block text-white hover:text-[#d4af37] transition-colors cursor-pointer text-left whitespace-nowrap"
                >
                  {t('Email Us', '给我们发邮件')}
                </button>
              </div>
            </div>

            <div className="md:col-span-3">
              <h4 className="text-[#d4af37] text-lg font-semibold mb-6">{t('Contact Us', '联系我们')}</h4>
              <div className="space-y-4">
                <div>
                  <a
                    href="mailto:ssdmysticacademy@gmail.com"
                    className="flex items-center space-x-2 text-[#d4af37] hover:text-[#f4d03f] transition-colors cursor-pointer group"
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-base group-hover:scale-110 transition-transform"></i>
                    </div>
                    <span className="text-sm">ssdmysticacademy@gmail.com</span>
                  </a>
                </div>
                <div>
                  <p className="text-[#c8b6ff] text-sm font-semibold mb-2">{t('Business Hours', '营业时间')}</p>
                  <p className="text-[#c8b6ff] text-sm">{t('Mon – Fri: 9:00 AM – 6:00 PM', '周一至周五：9:00 - 22:00')}</p>
                  <p className="text-[#c8b6ff] text-sm">{t('Weekends: 10:00 AM – 5:00 PM', '周末：10:00 - 23:00')}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-[#d4af37]/20 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-6 md:space-y-0">
              <div className="flex items-center space-x-6 order-1 md:order-1">
                {['ri-twitter-x-line', 'ri-facebook-line', 'ri-tiktok-line', 'ri-instagram-line'].map((icon) => (
                  <a key={icon} href="#" className="w-10 h-10 flex items-center justify-center text-[#d4af37] hover:text-white hover:rotate-12 transition-all duration-300 cursor-pointer">
                    <i className={`${icon} text-2xl`}></i>
                  </a>
                ))}
              </div>
              <div className="text-gray-400 text-sm text-center order-2 md:order-2">© SSD Mystic Academy. {t('All rights reserved.', '版权所有。')}</div>
              <div className="flex items-center space-x-6 order-3 md:order-3">
                <a href="#" className="text-white hover:text-[#d4af37] transition-colors text-sm cursor-pointer">{t('Privacy Policy', '隐私政策')}</a>
                <a href="#" className="text-white hover:text-[#d4af37] transition-colors text-sm cursor-pointer">{t('Terms of Service', '服务条款')}</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {contactOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setContactOpen(false)}></div>
          <div className="relative w-full max-w-md bg-[#0d0221] border border-[#d4af37]/40 rounded-2xl p-8">

            <button
              onClick={() => setContactOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-[#c8b6ff] hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {contactStatus === 'success' ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-[#d4af37]">
                  <i className="ri-checkbox-circle-line text-5xl"></i>
                </div>
                <h3 className="text-white text-xl font-semibold mb-2">{t('Message Sent!', '消息已发送！')}</h3>
                <p className="text-[#c8b6ff] text-sm mb-6">{t("We'll get back to you at your email soon.", '我们会尽快通过邮箱与您联系。')}</p>
                <button
                  onClick={() => setContactOpen(false)}
                  className="px-6 py-2 bg-[#d4af37] text-black rounded-full text-sm font-medium hover:bg-[#f4d03f] transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('Close', '关闭')}
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center text-[#d4af37]">
                    <i className="ri-mail-send-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-white text-lg font-semibold">{t('Contact Us', '联系我们')}</h3>
                    <p className="text-[#c8b6ff] text-xs">{t("Leave your info and we'll reply soon", '留下您的信息，我们会尽快回复')}</p>
                  </div>
                </div>

                <form data-readdy-form id="contact-us-form" onSubmit={handleContactSubmit} className="space-y-5">
                  <div>
                    <label className="block text-[#d4af37] text-xs mb-1.5">
                      {t('Your Name', '您的姓名')} <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={contactForm.name}
                      onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))}
                      placeholder={t('Enter your name', '请输入您的姓名')}
                      className="w-full px-0 py-2.5 bg-transparent border-b border-[#d4af37]/50 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#d4af37] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[#d4af37] text-xs mb-1.5">
                      {t('Email Address', '邮箱地址')} <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={contactForm.email}
                      onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))}
                      placeholder={t('Enter your email', '请输入您的邮箱')}
                      className="w-full px-0 py-2.5 bg-transparent border-b border-[#d4af37]/50 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#d4af37] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[#d4af37] text-xs mb-1.5">
                      {t('Message', '留言内容')} <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      value={contactForm.message}
                      onChange={e => setContactForm(f => ({ ...f, message: e.target.value }))}
                      placeholder={t('Tell us how we can help you...', '请告诉我们您的问题或需求...')}
                      className="w-full px-0 py-2.5 bg-transparent border-b border-[#d4af37]/50 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#d4af37] transition-colors resize-none"
                    />
                    <p className="text-right text-gray-500 text-xs mt-1">{contactForm.message.length}/500</p>
                  </div>

                  {contactStatus === 'error' && (
                    <p className="text-red-400 text-xs">{t('Something went wrong, please try again.', '提交失败，请稍后重试。')}</p>
                  )}

                  <button
                    type="submit"
                    disabled={contactStatus === 'submitting'}
                    className="w-full py-3 bg-[#d4af37] text-black rounded-full font-medium hover:bg-[#f4d03f] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center space-x-2"
                  >
                    {contactStatus === 'submitting' ? (
                      <><i className="ri-loader-4-line animate-spin"></i><span>{t('Sending...', '发送中...')}</span></>
                    ) : (
                      <><i className="ri-send-plane-line"></i><span>{t('Send Message', '发送消息')}</span></>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
